/**
 * Pure helpers extracted from `PromptLoginWidget` so they can be unit-
 * tested without spinning up RTL/jsdom. Kept side-effect-free.
 */
import { parsePhoneNumber } from 'awesome-phonenumber';

/** Validate a 10-digit US phone — same library + region as the text-side flow. */
export const isPhoneValid = (phone: string): boolean =>
  parsePhoneNumber(phone, { regionCode: 'US' }).valid;

/**
 * Progressive `(XXX) XXX-XXXX` mask. Empty / short inputs are returned as
 * raw digits so the input stays readable while the user types. Mirrors
 * `PromptLoginPart`'s text-side mask exactly.
 */
export const applyPhoneMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Strip every non-digit. Convenience used by the input onChange handlers. */
export const toDigits = (value: string): string => value.replace(/\D/g, '');

/**
 * Decide whether the speech-prefill timer should auto-submit. Returns
 *  - `'submit'` when the prefilled digits look complete (10 for phone,
 *    6 for otp) and pass any extra validation;
 *  - `'wait'` when they're partial — caller surfaces an error and asks
 *    the user to repeat / type the rest.
 *
 * Centralizing here keeps the auto-submit policy unit-testable.
 */
export function decideAutoSubmit(
  kind: 'phone' | 'otp',
  value: string,
): 'submit' | 'wait' {
  if (kind === 'phone') {
    if (value.length === 10 && isPhoneValid(value)) return 'submit';
    if (value.length === 11 && value.startsWith('1') && isPhoneValid(value.slice(1))) return 'submit';
    return 'wait';
  }
  return value.length === 6 ? 'submit' : 'wait';
}
